import React, { useCallback } from 'react'
import { useTranslation } from 'contexts/Localization'
import { CheckmarkIcon, InjectedModalProps, Modal } from '@nominex/dex-uikit'
import { SlButton } from '@shoelace-style/shoelace/dist/react'
import { useWagmi } from 'libs/wagmi'
import { Currency, CurrencyAmount, FactoryVersion, Token } from '@nominex/nomiswap-sdk'
import { Field } from 'state/mint/actions'
import { IconWrapper } from 'components/styles'
import { PairState, usePair } from 'hooks/usePairs'
import { AutoColumn } from 'components/Layout'
import { isPairWithCurrenciesExist } from 'utils'
import { STABLE_SWAP_PAIRS } from 'config/constants/exchange'
import StakeLpTokensCard from './StakeLpTokensCard'
import { ConfirmationPendingContent } from '.'

interface AddLiquidityModalProps {
  title: string
  hash: string | undefined
  customOnDismiss?: () => void
  content: () => React.ReactNode
  attemptingTxn: boolean
  pendingText: string
  currencyToAdd?: Currency | undefined
  currencies?: { [field in Field]?: Currency }
  mintedValue?: CurrencyAmount<Token>
}

const AddLiquidityModal: React.FC<InjectedModalProps & AddLiquidityModalProps> = ({
  title,
  onDismiss,
  customOnDismiss,
  attemptingTxn,
  hash,
  pendingText,
  content,
  currencyToAdd,
  currencies,
  mintedValue,
}) => {
  const { chainId } = useWagmi()
  const { t } = useTranslation()

  const currencyA = currencies[Field.CURRENCY_A]
  const currencyB = currencies[Field.CURRENCY_B]

  const stableSwapPairs = STABLE_SWAP_PAIRS[chainId] ?? []
  const isStableSwapPool = isPairWithCurrenciesExist(stableSwapPairs, currencyA, currencyB)

  const [pairState, pair] = usePair(currencyA, currencyB, isStableSwapPool ? FactoryVersion.V2 : FactoryVersion.V1)

  const handleDismiss = useCallback(() => {
    if (customOnDismiss) {
      customOnDismiss()
    }
    onDismiss()
  }, [customOnDismiss, onDismiss])

  if (!chainId) return null

  return (
    <Modal title={title} onDismiss={handleDismiss}>
      {attemptingTxn ? (
        <ConfirmationPendingContent pendingText={pendingText} />
      ) : hash ? (
        <AutoColumn justify="center" gap="18px">
          <IconWrapper bgColor="success" size="64px">
            <CheckmarkIcon color="success" />
          </IconWrapper>
          <div className="flex flex-col items-center gap-8">
            <div className="font-medium text-gray-200">{t('Liquidity added')}</div>
            <div className="text-gray-200">
              {t('You receive %amount% %symbol%', {
                amount: mintedValue?.toSignificant(6) || 0,
                symbol: currencyToAdd?.symbol,
              })}
            </div>
          </div>
          {pairState === PairState.EXISTS &&
            false && ( // TODO: need to check if farm exists
              <StakeLpTokensCard currencyA={currencyA} currencyB={currencyB} pair={pair} />
            )}
          <SlButton variant="primary" onClick={handleDismiss}>
            {t('Close')}
          </SlButton>
        </AutoColumn>
      ) : (
        content()
      )}
    </Modal>
  )
}

export default AddLiquidityModal
